import { createGraphClient } from '~/client/graph'
import registerMobileEvents from '~/client/src/mobile/stores/EventStore/mobileEvents'
import MobileEventStore from '~/client/src/mobile/stores/EventStore/MobileEvents.store'
import MobileInitialState from '~/client/src/mobile/stores/MobileInitialState'
import MobileRootStore from '~/client/src/mobile/stores/MobileRoot.store'
import Configuration, { IAppConfig } from '~/client/src/shared/Config'
import ApiAuthService from '~/client/src/shared/services/ApiAuthService'
import StruxHubAuthService from '~/client/src/shared/services/StruxHubAuthService'
import registerCommonEvents from '~/client/src/shared/stores/EventStore/commonEvents'
import EffectsProcessor from '~/client/src/shared/stores/EventStore/EffectsProcessors/EffectsProcessor'
import FlowProcessor from '~/client/src/shared/stores/EventStore/EffectsProcessors/FlowProcessor/FlowProcessor'

import CommonContainer from '../shared/CommonContainer'
import ProcoreService from '../shared/services/ProcoreService'
import GraphProcessor from '../shared/stores/EventStore/EffectsProcessors/GraphProcessors/GraphProcessor'
import WorkerProcessor from '../shared/stores/EventStore/EffectsProcessors/WorkerProcessor/WorkerProcessor'
import GraphExecutor from '../shared/utils/GraphExecutor'

export default class Container extends CommonContainer {
  private configuration: IAppConfig = Configuration
  private $rootStore: MobileRootStore
  private initialState: MobileInitialState
  protected eventsStore: MobileEventStore
  private struxHubAuthService: StruxHubAuthService
  private apiAuthService: ApiAuthService
  private procoreService: ProcoreService
  private graphExecutor: GraphExecutor

  public constructor(private isTestEnv: boolean) {
    super()

    this.initialState = new MobileInitialState()

    this.procoreService = new ProcoreService(this.initialState)

    this.graphExecutor = new GraphExecutor(this.initialState)

    this.apiAuthService = new ApiAuthService(
      this.initialState,
      this.graphExecutor,
    )

    this.struxHubAuthService = new StruxHubAuthService(this.apiAuthService)

    const getValidToken = () => this.struxHubAuthService.getValidAccessToken()
    const forceRefreshToken = () =>
      this.struxHubAuthService.refreshAccessToken()

    // Route REST refreshes (ApiAuthService.updateAccessToken) through the
    // GraphQL service's dedup gate so concurrent REST + GraphQL 401s share a
    // single network refresh instead of racing and revoking each other.
    this.apiAuthService.setRefreshAccessTokenDelegate(forceRefreshToken)

    // Create the graph client ONCE here with the proper URL.
    // Previously this was a tenantless (uri:null) client and `initAuthUser`
    // replaced it after login — but replacing the client orphans React-Apollo
    // hook subscriptions (responses flow to the new client's listeners; old
    // subscribers stay in `loading: true` forever). Keeping one stable client
    // for the app lifetime preserves subscriptions; auth is resolved per
    // request via `getValidToken`. forceRefreshToken backs Apollo's 401
    // retry path.
    this.initialState.graphClient = createGraphClient(
      getValidToken,
      forceRefreshToken,
      location.host,
      this.onSubscriptionsReconnected,
    )

    this.eventsStore = new MobileEventStore(
      new EffectsProcessor(
        getValidToken,
        new FlowProcessor(),
        new WorkerProcessor(),
        new GraphProcessor(this.initialState, this.graphExecutor),
      ),
      this.initialState,
    )

    registerCommonEvents(this.eventsStore, this.rootStore)
    registerMobileEvents(this.eventsStore, this.rootStore)

    this.registerGlobalEventListeners()
  }

  public get rootStore() {
    if (!this.$rootStore) {
      this.$rootStore = new MobileRootStore(
        this.configuration,
        this.initialState,
        this.eventsStore,
        this.apiAuthService,
        this.struxHubAuthService,
        this.procoreService,
        this.graphExecutor,
        this.isTestEnv,
      )
    }

    return this.$rootStore
  }
}
